/* eslint-disable @typescript-eslint/no-empty-function */
import React from "react"
import PropTypes from "prop-types"
import { connect } from "react-redux"
import classNames from "classnames"

// components
import SmallSheet from "app/static/frontend/sheets/components/SmallSheet"
import BigSheet from "app/static/frontend/sheets/components/BigSheet"
import LoadingButton from "app/static/frontend/components/LoadingButton" // eslint-disable-line import/no-named-as-default
import SheetsMapWrapper from "app/static/frontend/sheets/components/SheetsMapWrapper"

// redux
import * as actionCreators from "app/static/frontend/sheets/sheet-action-creators"

import { publicSheetContainerConnection, createSheetKey } from "app/static/frontend/sheets/selectors/sheet-selectors"

import Icon from "app/static/frontend/components/Icon"
import { Row, Col, Button, FormGroup, FormControl, InputGroup } from "react-bootstrap"
import LoadingSpinner from "components/LoadingSpinner"
import sheetConstants from "sheets/constants"
import { swalContents } from "sheets/components/helpTexts"

import { decodeStyles } from "imports/styleHelperFunctions"
import sheetVars from "app/static/stylus/sheets-variables.json"
import BACKENDERROR from "app/static/frontend/imports/backenderror"
import { INCORRECT_EXTERNAL_PASSWORD } from "components/ExternalPasswordWrapper"

import SimpleNavBar from "containers/SimpleNavBar"

import "app/static/stylus/public-sheet-container.styl"
import "app/static/stylus/FormHeader.styl"

const sheetsVariables = decodeStyles(sheetVars)
/* This Container is used to render public spreadsheets  */
// when the page has a map, hot reloading does not work
export class PublicSheetContainer extends React.Component {
    constructor(props) {
        super(props)

        // we need this to know whether to show the Map button in public sheets
        this.state = { showMapButton: undefined }
    }

    UNSAFE_componentWillMount = async () => {
        this.props.initSheet(createSheetKey(this.props.sheetId), { isPublicSheetMode: true })

        try {
            // Public Sheets always fetch the cached column data
            this.networkRequest = await this.props.loadPublicSheet(
                createSheetKey(this.props.sheetId),
                this.props.sheetId,
            )
        } catch (error) {
            if (error && error.response && error.response.status === 404) {
                await swal({ ...swalContents.failLoadSheet, text: "This sheet does not exist" })
            } else if (error && error.response && error.response.status === 401) {
                const refresh = await swal(INCORRECT_EXTERNAL_PASSWORD)
                if (refresh) {
                    document.location.reload()
                }
            } else {
                await BACKENDERROR(error, swalContents.failLoadSheet)
            }
        }

        // if we have a default sort applied, make sure to apply it
        if (this.props.defaultPublicSortColumnIndex || this.props.defaultPublicSortColumnIndex === 0) {
            this.props.sortByColumnAtIndex(
                this.props.id,
                this.props.defaultPublicSortColumnIndex,
                this.props.defaultPublicSortColumnType,
            )
        }
    }

    // hacky way to use the show_map boolean value to initially render or not render
    // a show map button. Since the user has control over this field, we can't rely on it
    // directly
    UNSAFE_componentWillReceiveProps(nextProps) {
        // if this.state.showMapButton is true, then it means it was set to true from an api response
        // everywhere we set defaults for it we have it set to false
        // if its true, ignore any further updates to prevent the user from overwriting
        if (this.state.showMapButton === true) {
            // don't do anything
        } else {
            this.setState({ showMapButton: nextProps.show_map }) // eslint-disable-line no-unused-vars
        }
    }

    // handle the sheet download
    download = async () => {
        const key = createSheetKey(this.props.sheetId)
        const downloadArguments = Object.assign({ is_public_sheet_download: true }, this.props.downloadArguments)
        try {
            await this.props.downloadSheet(key, downloadArguments)
        } catch (error) {
            await BACKENDERROR(error, swalContents.failDownloadSheet)
        }
    }

    // handle searching from the search box
    search = (event) => {
        this.props.searchByColumnAtIndex(this.props.id, sheetConstants.DATUM_COLUMN_INDEX, event.target.value)
    }

    // the FormComponent doesn't seem to like undefined values, so pamper it with an empty string
    // if that's what we get from the selector
    handleSearchValue = () => {
        const { searchingColumnValues, searchingColumnIndexes } = this.props

        if (!searchingColumnIndexes || !searchingColumnValues) {
            return ""
        }
        if (!searchingColumnIndexes.includes(sheetConstants.DATUM_COLUMN_INDEX)) {
            return ""
        }
        // the value 0 or other variants of falsey are not falsey in our scenario
        const value = searchingColumnValues.get(String(sheetConstants.DATUM_COLUMN_INDEX))
        if (value === undefined || value === null) {
            return ""
        } else {
            return value
        }
    }

    toggleMap = () => this.props.toggleShowMap(this.props.id)
    downloadMap = () => window.graphSVGDownload("#advanced-search-map-svg", this.props.name, this.props.sheetId)

    renderSheet() {
        if (this.props.sheetInitiallyLoading) {
            return <LoadingSpinner id="page-loading-no-top" />
        }

        // render sheets with limited permissions
        if (this.props.shouldLoadFullSheetIntoMemory) {
            return (
                <SmallSheet
                    canAddColumns={false}
                    canDownload
                    canRemoveColumns={false}
                    canRemoveRows={false}
                    canRenameColumns={false}
                    canRenameSheet={false}
                    id={createSheetKey(this.props.sheetId)}
                    isReorderable={this.props.allow_public_sheet_reorder_columns}
                    mapFilterValues={this.props.mapFilterValues} // pass these down for sheets with map
                    publicSheet
                    sheetId={this.props.sheetId}
                    show_map={this.props.show_map} // help ResponsiveSheet compute correctly
                    showBackButton={false}
                />
            )
        }

        return (
            <BigSheet
                canAddColumns={false}
                canDownload
                canRemoveColumns={false}
                canRemoveRows={false}
                canRenameColumns={false}
                canRenameSheet={false}
                id={createSheetKey(this.props.sheetId)}
                isReorderable={this.props.allow_public_sheet_reorder_columns}
                publicSheet
                sheetId={this.props.sheetId}
                showBackButton={false}
            />
        )
    }

    renderContainer() {
        return (
            <div
                className={classNames("public-sheet-grid", {
                    "public-sheet-with-map-grid": this.props.show_map,
                    "public-sheet-with-description-grid":
                        this.props.shouldShowDescription && this.props.description && !this.props.show_map,
                    "public-sheet-with-map-and-description-grid":
                        this.props.shouldShowDescription && this.props.description && this.props.show_map,
                })}
            >
                {/* Search Bar and buttons */}
                <Row>
                    <Col md={12}>
                        <Row className="form-header-row public-sheet-header" id="sheet-header">
                            <div className="page-header form-header">
                                <FormGroup className="form-group">
                                    <InputGroup>
                                        <FormControl
                                            size="md"
                                            className="public-sheet-search-bar-container form-control"
                                            id="public-sheets-search-bar"
                                            onChange={this.search}
                                            placeholder={this.props.searchBarPlaceHolder}
                                            value={this.handleSearchValue()}
                                        />
                                        <InputGroup.Text className="input-group-addon">
                                            <Icon name="search" />
                                        </InputGroup.Text>
                                    </InputGroup>
                                </FormGroup>
                                <Button
                                    size="md"
                                    variant="primary"
                                    className={classNames({
                                        "sheets-buttons": this.state.showMapButton,
                                        "hide-show-map-button":
                                            !this.state.showMapButton || !this.props.shouldLoadFullSheetIntoMemory,
                                    })}
                                    data-cy="toggle-map"
                                    onClick={this.toggleMap}
                                >
                                    <Icon name="map" />
                                    {this.props.show_map ? "Hide Map" : "Show Map"}
                                </Button>
                                {this.props.show_map && (
                                    <Button
                                        id="download-map"
                                        size="md"
                                        variant="primary"
                                        className={classNames({
                                            "sheets-buttons": this.state.showMapButton,
                                            "hide-show-map-button":
                                                !this.state.showMapButton || !this.props.shouldLoadFullSheetIntoMemory,
                                        })}
                                        data-cy="download-map"
                                        onClick={this.downloadMap}
                                    >
                                        Download Map
                                    </Button>
                                )}
                                <LoadingButton
                                    block
                                    variant="primary"
                                    bsSize="md"
                                    buttonText="Download"
                                    className="sheets-buttons"
                                    icon={<Icon name="download" />}
                                    id="download-sheet"
                                    isLoading={this.props.isDownloading}
                                    loadingText={"Downloading"}
                                    onClick={this.download}
                                />
                            </div>
                        </Row>
                    </Col>
                </Row>

                {/* Public Sheet description */}
                {this.props.shouldShowDescription && this.props.description && (
                    <Row>
                        <div
                            className="public-sheet-description"
                            dangerouslySetInnerHTML={{ __html: this.props.description }}
                        />
                    </Row>
                )}

                {/* Map */}
                <Row className="text-center">
                    <SheetsMapWrapper
                        addMapFilter={this.props.addMapFilter}
                        show_map={this.props.show_map}
                        mapData={this.props.mapData}
                        mapTarget="public-sheets-map-target"
                        quorumDataType={this.props.dataType}
                        removeMapFilter={this.props.removeMapFilter}
                        sheetId={this.props.id}
                    />
                </Row>

                {/* Sheet */}
                <Row
                    id="sheet-wrapper"
                    className={classNames("public-sheet-row-wrapper", {
                        "big-sheet-wrapper": !this.props.shouldLoadFullSheetIntoMemory,
                    })}
                >
                    <Col md={12} className="sheet-wrapper-inner public-sheet" id={this.props.id}>
                        {this.renderSheet()}
                    </Col>
                </Row>
            </div>
        )
    }

    render() {
        return (
            <div>
                <SimpleNavBar image_url={this.props.organizationLogo} sheetName={this.props.sheetName} />
                <div id="container">
                    <Row className="main-public-sheets-container">
                        <Col xs={12} className="main-public-sheets">
                            {this.renderContainer()}
                        </Col>
                    </Row>
                </div>
            </div>
        )
    }
}

PublicSheetContainer.propTypes = {
    canHideColumns: PropTypes.bool,
    defaultPublicSortColumnIndex: PropTypes.number,
    downloadSheet: PropTypes.func.isRequired,

    // helps to determine how to limit functionality in public sheets
    isPublicSheetMode: PropTypes.bool.isRequired,
    params: PropTypes.shape({
        sheetId: PropTypes.string.isRequired,
    }).isRequired,
    shouldLoadFullSheetIntoMemory: PropTypes.bool.isRequired,
    show_map: PropTypes.bool,
    toggleShowMap: PropTypes.func.isRequired,
}

PublicSheetContainer.defaultProps = {
    canHideColumns: false,
    show_map: false, // we need this value to be false because the logic to hide the Show Map button depends on this to be false
    initSheet: () => {},
    params: {
        shouldLoadFullSheetIntoMemory: true,
    },
    sheetId: "",
}

export default connect(publicSheetContainerConnection, {
    ...actionCreators,
})(PublicSheetContainer)
